import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { artavaBrand, getArtavaCopy } from '../../branding/artavaBrand';
import styles from './CompanyPresenceDock.module.css';

const AUTH_LOCKUP_ROUTES = new Set(['/', '/login', '/register']);
const DISMISS_DURATION_MS = 5 * 60 * 1000;
const DISMISS_STORAGE_KEY = 'smartiCompanyDockHiddenUntil';

function readHiddenUntil() {
  if (typeof window === 'undefined') return 0;
  let rawValue = 0;
  try {
    rawValue = Number(window.localStorage.getItem(DISMISS_STORAGE_KEY) || 0);
  } catch (error) {
    rawValue = 0;
  }
  return Number.isFinite(rawValue) ? rawValue : 0;
}

export default function CompanyPresenceDock() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const normalizedPath = String(location.pathname || '/').toLowerCase();
  const [hiddenUntil, setHiddenUntil] = useState(readHiddenUntil);
  const [isDismissed, setIsDismissed] = useState(() => readHiddenUntil() > Date.now());
  const isItalian = i18n.language?.startsWith('it');

  const artavaCopy = getArtavaCopy(isItalian);
  const copy = useMemo(() => (
    isItalian
      ? {
          eyebrow: artavaBrand.companyName,
          headline: 'Serve nuovo software industriale?',
          description: 'Scopri chi sviluppa Smarti e apri un contatto tecnico con Artava.',
          website: artavaBrand.websiteHost,
          supportLabel: 'Apri pagina Artava',
          closeLabel: 'Chiudi il pannello Artava Systems',
        }
      : {
          eyebrow: artavaBrand.companyName,
          headline: 'Need new industrial software?',
          description: 'See who builds Smarti and start a technical conversation with Artava.',
          website: artavaBrand.websiteHost,
          supportLabel: 'Open Artava page',
          closeLabel: 'Close the Artava Systems panel',
        }
  ), [isItalian]);

  const dismissDock = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDismissed(true);
    setHiddenUntil(Date.now() + DISMISS_DURATION_MS);
  };

  useEffect(() => {
    if (!hiddenUntil || hiddenUntil <= Date.now()) {
      setIsDismissed(false);
      try {
        window.localStorage.removeItem(DISMISS_STORAGE_KEY);
      } catch (error) {
        // Ignore storage failures and keep the dock visible.
      }
      return undefined;
    }

    const remainingMs = hiddenUntil - Date.now();
    setIsDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, String(hiddenUntil));
    } catch (error) {
      // Ignore storage failures and still hide during this session.
    }
    const timeoutId = window.setTimeout(() => {
      setHiddenUntil(0);
      setIsDismissed(false);
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [hiddenUntil]);

  if (AUTH_LOCKUP_ROUTES.has(normalizedPath) || isDismissed) {
    return null;
  }

  return (
    <aside className={styles.dock} aria-label="Artava Systems company identity">
      <a
        className={styles.logoLink}
        href={artavaBrand.websiteUrl}
        target="_blank"
        rel="noreferrer"
      >
        <img
          src={artavaBrand.logoSrc}
          alt={artavaBrand.companyName}
          className={styles.logo}
        />
      </a>

      <div className={styles.meta}>
        <div className={styles.metaHeader}>
          <p className={styles.eyebrow}>{copy.eyebrow}</p>
          <button
            type="button"
            className={styles.closeButton}
            onMouseDown={dismissDock}
            onClick={dismissDock}
            aria-label={copy.closeLabel}
          >
            x
          </button>
        </div>
        <p className={styles.headline}>{copy.headline}</p>
        <p className={styles.description}>{copy.description}</p>
        <div className={styles.actionRow}>
          <a
            className={styles.website}
            href={artavaBrand.websiteUrl}
            target="_blank"
            rel="noreferrer"
          >
            {copy.website}
          </a>
          <Link className={styles.supportLink} to="/support/artava">
            {copy.supportLabel}
          </Link>
        </div>
        <span className={styles.caption}>{artavaCopy.attributionDetail}</span>
      </div>
    </aside>
  );
}
